import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import SiteLocationMap from "@/components/site-location-map";
import {
  FieldErrorMessage,
  FormErrorAlert,
} from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getFirstErrorMessage } from "@/lib/form-errors";
import { encodePlusCode } from "@/lib/plus-codes";
import {
  type EntranceFormApi,
  entranceFormSchema,
  type SiteEntrance,
} from "./entrance-types";

interface EntranceFormProps {
  entrance?: SiteEntrance;
  onSave?: (entrance: SiteEntrance) => void | Promise<void>;
  onCancel?: () => void;
  siteLat?: number;
  siteLon?: number;
  submitError?: string | null;
  onClearSubmitError?: () => void;
}

function EntranceNameField({
  form,
  onFieldInteraction,
}: {
  form: EntranceFormApi;
  onFieldInteraction?: () => void;
}) {
  return (
    <form.Field name="name">
      {(field) => {
        const fieldError = getFirstErrorMessage(
          field.state.meta.errors as ReadonlyArray<unknown>
        );

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Entrance Name</Label>
            <Input
              id={field.name}
              name={field.name}
              value={field.state.value}
              aria-invalid={Boolean(fieldError)}
              onBlur={field.handleBlur}
              onChange={(e) => {
                onFieldInteraction?.();
                field.handleChange(e.target.value);
              }}
              placeholder="e.g., Main Gate"
            />
            <FieldErrorMessage message={fieldError} />
          </div>
        );
      }}
    </form.Field>
  );
}

function EntranceLocationField({
  form,
  onLocationChange,
  siteLat,
  siteLon,
  onFieldInteraction,
}: {
  form: EntranceFormApi;
  onLocationChange?: () => void;
  siteLat?: number;
  siteLon?: number;
  onFieldInteraction?: () => void;
}) {
  return (
    <form.Field name="lat">
      {(latField) => {
        const lat = latField.state.value;
        const lon = form.getFieldValue("lon");
        const locationError = getFirstErrorMessage(
          latField.state.meta.errors as ReadonlyArray<unknown>
        );
        const hasPosition =
          typeof lat === "number" &&
          Number.isFinite(lat) &&
          typeof lon === "number" &&
          Number.isFinite(lon);

        const mapLat = hasPosition
          ? (lat as number)
          : typeof siteLat === "number"
            ? siteLat
            : 51.505;
        const mapLon = hasPosition
          ? (lon as number)
          : typeof siteLon === "number"
            ? siteLon
            : -0.09;

        return (
          <div className="space-y-2">
            <Label>Entrance Location</Label>
            <EntranceMapWithRadius
              lat={mapLat}
              lon={mapLon}
              form={form}
              onPositionChange={(next) => {
                onFieldInteraction?.();
                form.setFieldValue("lat", next.lat);
                form.setFieldValue("lon", next.lon);
                const normalized = encodePlusCode(next.lat, next.lon);
                form.setFieldValue("plusCode", normalized);
                onLocationChange?.();
              }}
            />
            <p className="text-xs text-muted-foreground">
              {hasPosition
                ? "Drag the marker or tap the map to adjust the entrance location."
                : "Click on the map to set the entrance location."}
            </p>
            <FieldErrorMessage message={locationError} />
          </div>
        );
      }}
    </form.Field>
  );
}

function EntranceMapWithRadius({
  lat,
  lon,
  form,
  onPositionChange,
}: {
  lat: number;
  lon: number;
  form: EntranceFormApi;
  onPositionChange: (coords: { lat: number; lon: number }) => void;
}) {
  const geofenceDistance = form.getFieldValue("geofenceDistance");
  const radius = useMemo(() => {
    const parsed = Number(geofenceDistance);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 100;
  }, [geofenceDistance]);

  return (
    <SiteLocationMap
      lat={lat}
      lon={lon}
      radius={radius}
      onPositionChange={onPositionChange}
    />
  );
}

function EntranceGeofenceField({
  form,
  onFieldInteraction,
}: {
  form: EntranceFormApi;
  onFieldInteraction?: () => void;
}) {
  return (
    <form.Field name="geofenceDistance">
      {(field) => {
        const fieldError = getFirstErrorMessage(
          field.state.meta.errors as ReadonlyArray<unknown>
        );
        const options = [
          { value: "50", label: "50 meters" },
          { value: "100", label: "100 meters" },
          { value: "200", label: "200 meters" },
          { value: "500", label: "500 meters" },
          { value: "1000", label: "1000 meters" },
        ];

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Geofence Distance</Label>
            <Select
              value={field.state.value}
              onValueChange={(value) => {
                onFieldInteraction?.();
                field.handleChange(value);
              }}
            >
              <SelectTrigger
                className="w-full"
                aria-invalid={Boolean(fieldError)}
              >
                <SelectValue placeholder="Select distance" />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldErrorMessage message={fieldError} />
          </div>
        );
      }}
    </form.Field>
  );
}

export default function EntranceForm({
  entrance,
  onSave,
  onCancel,
  siteLat,
  siteLon,
  submitError,
  onClearSubmitError,
}: EntranceFormProps) {
  const form = useForm({
    defaultValues: {
      name: entrance?.name || "",
      geofenceDistance: entrance?.geofenceDistance
        ? String(entrance.geofenceDistance)
        : "50", // Default to 50m for entrances (smaller than site default)
      lat: entrance?.lat ?? (typeof siteLat === "number" ? siteLat : null),
      lon: entrance?.lon ?? (typeof siteLon === "number" ? siteLon : null),
      plusCode: entrance?.plusCode ?? "",
    },
    validators: {
      onSubmit: entranceFormSchema,
    },
    onSubmit: async ({ value }) => {
      const lat = value.lat as number;
      const lon = value.lon as number;
      const entranceData: SiteEntrance = {
        id: entrance?.id,
        name: value.name.trim(),
        geofenceDistance: parseInt(value.geofenceDistance, 10),
        lat,
        lon,
        plusCode: value.plusCode.trim() || undefined,
      };
      await onSave?.(entranceData);
    },
  });

  return (
    <div className="p-4 bg-card border border-border rounded-lg">
      <div className="md:grid md:grid-cols-2 md:gap-6 space-y-4 md:space-y-0">
        {/* Left: fields + actions */}
        <div className="space-y-4 flex flex-col">
          <EntranceNameField
            form={form}
            onFieldInteraction={onClearSubmitError}
          />
          <EntranceGeofenceField
            form={form}
            onFieldInteraction={onClearSubmitError}
          />
          <FormErrorAlert message={submitError} />
          <div className="flex flex-col gap-2 md:mt-auto">
            <form.Subscribe>
              {(state) => (
                <Button
                  type="button"
                  disabled={!state.canSubmit || state.isSubmitting}
                  onClick={form.handleSubmit}
                >
                  {entrance ? "Update Entrance" : "Add Entrance"}
                </Button>
              )}
            </form.Subscribe>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
        {/* Right: map */}
        <div>
          <EntranceLocationField
            form={form}
            siteLat={siteLat}
            siteLon={siteLon}
            onFieldInteraction={onClearSubmitError}
          />
        </div>
      </div>
    </div>
  );
}
